import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import { CommentDto, Broker } from "../../../../../../api/Api";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  creditCheck: string;
  creditCheckAssignee: string;
  creditAmount: number | undefined;
  creditCurrency: string;
  comments: CommentDto[];
  signOffNote: string;
};

/** Split out of Risk: the credit check, its amount and currency. Worked by the Risk role. */
export const CreditRisk: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(
    specificBroker,
    "creditRisk"
  );

  const creditRisk = specificBroker?.creditRiskLifecycle;
  const [formData, setFormData] = useState<FormData>({
    creditCheck: creditRisk?.creditCheck || "",
    creditCheckAssignee: creditRisk?.creditCheckAssignee || "",
    creditAmount: creditRisk?.creditAmount ?? undefined,
    creditCurrency: creditRisk?.creditCurrency || "",
    comments: creditRisk?.comments || [],
    signOffNote: creditRisk?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const riskAssignees = useBrokerAssignees("risk");

  const updateBroker = useUpdateBroker({ onSuccess: handleClose });
  const commentUpdate = useUpdateBroker();

  const buildBroker = (values: FormData & { complete?: boolean }): Broker => ({
    ...specificBroker,
    creditRiskLifecycle: {
      ...specificBroker.creditRiskLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...formData, comments })
  });

  const handleChange = (
    field: keyof FormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificBroker.progress?.creditRisk,
    canSetInProgress: ownsLifecycle(roles, "creditrisk"),
    buildEntity: (values, progress) => {
      const saved = buildBroker(values);
      return {
        ...saved,
        progress: { ...saved.progress, creditRisk: progress }
      };
    },
    mutate: updateBroker.mutate
  });

  const open = location.pathname.endsWith("/creditrisk");

  // The credit amount is deliberately not required when approved: leaving it blank is how an
  // unlimited broker is recorded, matching M_CreditLimit NULL in the warehouse. The currency is
  // not required either: brokers never had to give one, unlike counterparties.
  const canComplete =
    formData.creditCheck === "approved" || formData.creditCheck === "rejected";

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Credit Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>Credit check</InputLabel>
            <Select
              label="Credit check"
              value={formData.creditCheck}
              onChange={(e) => handleChange("creditCheck", e.target.value)}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={riskAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              riskAssignees.data?.find(
                (a) => a.email === formData.creditCheckAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("creditCheckAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit check assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {formData.creditCheck === "approved" && (
          <>
            <TextField
              label="Credit amount"
              type="number"
              value={formData.creditAmount ?? ""}
              onChange={(e) =>
                handleChange(
                  "creditAmount",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              helperText="Leave blank for no limit (unlimited)"
              slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
            />

            <TextField
              label="Currency"
              value={formData.creditCurrency}
              onChange={(e) => handleChange("creditCurrency", e.target.value)}
              placeholder="e.g., USD, EUR, GBP"
            />
          </>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
